import React from 'react'
import styled from '@emotion/styled'
import { ResolvedDay } from '../types'
import { MONTH_LABELS, DAY_OF_WEEK_LABELS } from '../constants'
import { DayOfWeek } from '../types'
import { toISODate } from '../utils'
import CalendarDay from './CalendarDay'

interface Props {
  year: number
  month: number // 0-indexed
  resolvedDays: Map<string, ResolvedDay>
  holidays?: Map<string, string> // ISO date → holiday name; undefined = hidden
  todayStr: string
  onDayClick: (date: string) => void
}

const HEADER_DAYS = [
  DayOfWeek.Monday,
  DayOfWeek.Tuesday,
  DayOfWeek.Wednesday,
  DayOfWeek.Thursday,
  DayOfWeek.Friday,
  DayOfWeek.Saturday,
  DayOfWeek.Sunday,
]

/**
 * Returns the day-of-week offset for the first of the month (0=Mon, 6=Sun).
 */
function getStartOffset(year: number, month: number): number {
  const firstDay = new Date(year, month, 1).getDay() // 0=Sun

  return firstDay === 0 ? 6 : firstDay - 1
}

const CalendarMonth: React.FC<Props> = ({
  year,
  month,
  resolvedDays,
  holidays,
  todayStr,
  onDayClick,
}) => {
  const daysInMonth = new Date(year, month + 1, 0).getDate()
  const startOffset = getStartOffset(year, month)

  // Count holidays in this month for the title badge
  let holidayCount = 0
  if (holidays) {
    for (let d = 1; d <= daysInMonth; d++) {
      const dateStr = toISODate(new Date(year, month, d))
      if (holidays.has(dateStr)) {
        holidayCount++
      }
    }
  }

  const cells: React.ReactNode[] = []

  // Empty cells for offset
  for (let i = 0; i < startOffset; i++) {
    cells.push(<EmptyCell key={`empty-${i}`} />)
  }

  // Day cells
  for (let d = 1; d <= daysInMonth; d++) {
    const date = new Date(year, month, d)
    const dateStr = toISODate(date)
    const resolved = resolvedDays.get(dateStr)
    const jsDay = date.getDay()
    const isWeekend = jsDay === 0 || jsDay === 6

    if (resolved) {
      cells.push(
        <CalendarDay
          key={dateStr}
          day={resolved}
          dayOfMonth={d}
          isWeekend={isWeekend}
          isToday={dateStr === todayStr}
          holidayName={holidays?.get(dateStr)}
          onClick={onDayClick}
        />,
      )
    }
  }

  return (
    <MonthContainer>
      <MonthTitle>{MONTH_LABELS[month]}</MonthTitle>
      {holidays && holidayCount > 0 && (
        <HolidayCount>
          {holidayCount} National Holiday{holidayCount !== 1 ? 's' : ''}
        </HolidayCount>
      )}
      <UnifiedGrid>
        {HEADER_DAYS.map((day) => (
          <DayHeader key={day}>{DAY_OF_WEEK_LABELS[day]}</DayHeader>
        ))}
        {cells}
      </UnifiedGrid>
    </MonthContainer>
  )
}

export default CalendarMonth

// ── Styled Components ──

const MonthContainer = styled.div``

const MonthTitle = styled.h4`
  margin: 0 0 6px;
  font-size: 13px;
  font-weight: 600;
  color: #212121;
  text-align: center;
`

const HolidayCount = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: #ff8f00;
  text-align: center;
  margin-bottom: 4px;
`

/* Single grid for both header row and day cells — guarantees column alignment */
const UnifiedGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 2px;
`

const DayHeader = styled.div`
  text-align: center;
  font-size: 9px;
  font-weight: 600;
  color: #9e9e9e;
  padding: 2px 0 4px;
`

const EmptyCell = styled.div`
  min-height: 68px;
`
